import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useState, useEffect } from "react";

interface OnboardingStep {
	emoji: string;
	title: string;
	description: string;
	gifPath: string;
}

interface FirstTimeOnboardingProps {
	isOpen: boolean;
	onComplete: () => void;
	onSkip: () => void;
	steps: OnboardingStep[];
	container?: HTMLElement | null;
}

export function FirstTimeOnboarding({ isOpen, onComplete, onSkip, steps, container }: FirstTimeOnboardingProps) {
	const [currentStep, setCurrentStep] = useState(0);
	const [videoError, setVideoError] = useState(false);
	const isLastStep = currentStep === steps.length - 1;

	const handleNext = () => {
		if (isLastStep) {
			onComplete();
		} else {
			setCurrentStep(currentStep + 1);
			setVideoError(false);
		}
	};

	const currentStepData = steps[currentStep];
	if (!currentStepData) return null;

	const videoSrc = `${window.location.origin}${currentStepData.gifPath}`;

	return (
		<Dialog open={isOpen}>
			<DialogContent container={container} showCloseButton={false} variant="default" className="bg-white border border-gray-200 rounded-lg shadow-lg text-black max-w-[400px] p-0 overflow-hidden" onOpenAutoFocus={(e) => e.preventDefault()}>
				<div className="flex flex-col">
					<div className="relative w-full h-48 overflow-hidden bg-gray-100">
						{!videoError ? (
							<video
								key={currentStep}
								src={videoSrc}
								autoPlay
								loop
								muted
								playsInline
								className="w-full h-full object-cover"
								onError={(e) => {
									console.error("Video failed to load:", videoSrc, e);
									setVideoError(true);
								}}
							/>
						) : (
							<div className="w-full h-full flex items-center justify-center text-6xl">
								{currentStepData.emoji}
							</div>
						)}
					</div>

					<div className="p-6">
						<div className="text-center mb-6">
							<h2 className="text-lg font-semibold mb-2 text-black">
								{currentStepData.emoji} {currentStepData.title}
							</h2>
							<p className="text-sm text-gray-600 leading-relaxed">{currentStepData.description}</p>
						</div>

						<div className="flex justify-between items-center">
							<button onClick={onSkip} className="text-sm px-4 py-2 text-gray-500 hover:text-gray-600 transition-colors cursor-pointer">
								Skip
							</button>

							<div className="flex justify-center">
								<div className="flex gap-2">
									{steps.map((_, index) => (
										<div key={index} className={`w-2 h-2 rounded-full transition-colors ${index === currentStep ? "bg-blue-500" : index < currentStep ? "bg-blue-300" : "bg-gray-300"}`} />
									))}
								</div>
							</div>

							<button onClick={handleNext} className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors cursor-pointer text-sm font-medium">
								{isLastStep ? "Done" : "Next"}
							</button>
						</div>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}
